// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useEffect } from 'react';

import {
  type GetPaginatedAllOpts,
  useEvolvePaginatedGetAll,
  type WorkCellTask,
  type WorkOrder,
  type WorkOrderPaginated,
} from '@evolvemep/api-ts';

import { isNil } from 'helpers/isNotNil';
import { useTaskStatuses } from 'hooks/typeCacheHooks/useTaskStatuses';

type ReturnType = {
  tasks: WorkCellTask[] | undefined;
  loading: boolean;
};

export const useStartedTasks = (workOrder: WorkOrder | WorkOrderPaginated): ReturnType => {
  const { states: taskStatusTypes } = useTaskStatuses();

  const fetchAllStartedTasksOpts = useMemo<GetPaginatedAllOpts<'shop/workCellTask'>>(
    () => ({
      lazy: isNil(taskStatusTypes),
      defaultConfig: {
        params: {
          orderBy: 'createdOn:asc',
          workOrderId: workOrder.workOrderId,
          taskStatusTypeIds: taskStatusTypes?.filter((t) => t.name !== 'Not Started').map((t) => t.stateId),
        },
      },
    }),
    [taskStatusTypes, workOrder.workOrderId],
  );

  const {
    data: allTasks,
    loading,
    setDefaultOpts,
  } = useEvolvePaginatedGetAll('shop/workCellTask', fetchAllStartedTasksOpts);

  useEffect(() => {
    setDefaultOpts(fetchAllStartedTasksOpts);
  }, [fetchAllStartedTasksOpts, setDefaultOpts]);

  return {
    tasks: allTasks,
    loading,
  };
};
